<script setup>
import { getMyVacancies } from '@/api/vacancyApi';
import { showLocaleValue } from '@/helper/helper';
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { useStore } from 'vuex';

const emit = defineEmits(['sendForm', 'close']);

const userData = reactive({
    name: { code: 5502, type: "str", value: null },
    email: { code: 5503, type: "str", value: null },
    type: { code: 5504, type: "str", value: 'Предложение' },
    text: { code: 5505, type: "text", value: null }
});

const clearForm = () => {
    userData.name.value = null;
    userData.email.value = null;
    userData.type.value = 'Предложение';
    userData.text.value = null;
};

const sendRequest = () => {
    requestLoading.value = true;
    emit('sendForm', userData, selectedVacancy.value, isAuthorized.value, (success) => {
        requestLoading.value = false; 
        if (success) {
            emit('close');
            clearForm();
        }
    });
};

const { smAndDown, mdAndDown } = useDisplay();
const { t } = useI18n();
const store = useStore();
const myResponses = ref([]);
const selectedVacancy = ref(null);
const requestLoading = ref(false);
const dictionaryLoading = ref(false);

const types = [
    { label: t('main.claim'), value: 'Претензия' },
    { label: t('main.offer'), value: 'Предложение' },
    { label: t('main.support'), value: 'Служба поддержки' },
];

const getResponses = async () => {
    dictionaryLoading.value = true;

    await getMyVacancies(2451)
        .then(res => {
            if (res && res.data) myResponses.value = res.data.results;
        })
        .finally(() => dictionaryLoading.value = false);
};

const isAuthorized = computed(() => {
    if (store.state?.token) return true;
    else return false;
});

const disabledBtn = computed(() => {
    const text = userData.text.value?.trim();
    const name = userData.name.value?.trim();
    const email = userData.email.value?.trim();
    const type = userData.type.value;

    if (isAuthorized.value) {
        return type === 'Жалоба на вакансию'
        ? !selectedVacancy.value || !text
        : !text;
    }
    return !name || !email || !text;
});

const getVacancyName = (vacancyProperties) => {
    if (!Array.isArray(vacancyProperties)) return '';
    
    const item = vacancyProperties.find(
        entry => entry?.indicator_meta?.code == 1002
    );

    return showLocaleValue(item?.indicator_value);
};

onMounted(() => {
    if (isAuthorized.value) types.push(
        { label: t('main.complaint'), value: 'Жалоба на вакансию' }
    );
});
</script>

<template>
    <v-dialog width="auto">
        <v-card 
            :width="smAndDown ? '85vw' : 760" 
            :class="['flex flex-col', smAndDown ? 'p-7' : 'p-10']"
        >
            <template v-if="!isAuthorized">
                <div :class="{ 'mb-1': !smAndDown }">
                    <v-label 
                        :text="$t('fields.name')" 
                        class="text-[#021C41] font-medium"
                        :class="smAndDown ? 'mb-1' : 'mb-2 text-xl'" 
                    />
                    <v-text-field 
                        variant="outlined"
                        base-color="#127CC1"
                        bg-color="#F3F6F9F5"
                        color="#127CC1"
                        v-model="userData.name.value"
                        :density="mdAndDown ? 'comfortable' : 'default'"
                    />
                </div>
                <div :class="{ 'mb-1': !smAndDown }">
                    <v-label 
                        :text="$t('fields.email')" 
                        class="text-[#021C41] font-medium"
                        :class="smAndDown ? 'mb-1' : 'mb-2 text-xl'" 
                    />
                    <v-text-field 
                        variant="outlined"
                        prepend-inner-icon="mdi-account-outline"
                        class="filled-icon"
                        placeholder="Логин"
                        base-color="#127CC1"
                        bg-color="#F3F6F9F5"
                        color="#127CC1"
                        v-model="userData.email.value"
                        :density="mdAndDown ? 'comfortable' : 'default'"
                    />
                </div>
            </template>
            <v-radio-group :inline="!smAndDown" v-model="userData.type.value">
                <v-radio 
                    v-for="type of types"
                    :key="type.label"
                    :label="type.label" 
                    :value="type.value" 
                    color="#2D9CDB" 
                    :density="smAndDown ? 'default' : 'compact'" 
                    class="mr-3" 
                />
            </v-radio-group>
            <v-select 
                v-if="userData.type.value === 'Жалоба на вакансию'"
                variant="outlined"
                :placeholder="$t('main.vacancy_response')"
                base-color="#127CC1"
                bg-color="#F3F6F9F5"
                color="#127CC1"
                :class="{ 'my-3': !smAndDown }"
                :menu-props="{ maxWidth: '600px' }"
                :density="mdAndDown ? 'comfortable' : 'default'"
                :items="myResponses"
                :item-title="item => getVacancyName(item.vacancy)"
                :item-value="item => item.vacancy_id"
                v-model="selectedVacancy"
                :loading="dictionaryLoading"
                @click="getResponses"
            />
            <div>
                <v-label 
                    :text="$t('admin.message')"
                    class="text-[#021C41] font-medium"
                    :class="smAndDown ? 'mb-1' : 'mb-2 text-xl'" 
                />
                <v-textarea 
                    variant="outlined"
                    density="compact"
                    base-color="#127CC1"
                    bg-color="#F3F6F9F5"
                    color="#127CC1"
                    v-model="userData.text.value"
                    no-resize
                    :rows="mdAndDown ? '3' : '5'"
                />
            </div>
            <div 
                class="flex justify-center"
                :class="smAndDown ? 'flex-col gap-4' : mdAndDown ? 'mt-2 gap-8' : 'mt-5 gap-8'"
            >
                <v-btn
                    flat
                    color="#127CC1"
                    :text="$t('btns.send')"
                    class="text-none font-normal"
                    tile
                    size="large"
                    rounded="lg"
                    height="44"
                    :disabled="disabledBtn"
                    :loading="requestLoading"
                    @click="sendRequest"
                />
                <v-btn
                    flat
                    color="#6B6B6B"
                    :text="$t('btns.cancel')"
                    class="text-none font-normal"
                    tile
                    size="large"
                    rounded="lg"
                    height="44"
                    @click="emit('close')"
                />
            </div>
        </v-card>
    </v-dialog>
</template>